import { format } from 'date-fns';
import { X, Check, Inbox, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';

interface PendingInvitationsSectionProps {
  invitations: PendingSupervisorInvitation[] | null;
  onAccept: (invitation: PendingSupervisorInvitation) => void;
  onDecline: (invitation: PendingSupervisorInvitation) => void;
  isProcessing?: boolean;
  decliningInvitationId?: string | null;
  /** When true, accept/decline actions are disabled (admin mode) */
  isDisabled?: boolean;
}

export function PendingInvitationsSection({
  invitations,
  onAccept,
  onDecline,
  isProcessing = false,
  decliningInvitationId = null,
  isDisabled = false,
}: PendingInvitationsSectionProps) {
  if (!invitations || invitations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Inbox className="mb-4 h-12 w-12 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-900">No pending invitations</h3>
        <p className="mt-1 text-sm text-gray-500">
          When someone invites you to join their organization, it will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {invitations.map(invitation => {
        const supervisorName =
          invitation.supervisorFirstName && invitation.supervisorLastName
            ? `${invitation.supervisorFirstName} ${invitation.supervisorLastName}`
            : invitation.supervisorEmail;

        const orgName =
          invitation.supervisorOrganizationDisplayName ||
          invitation.supervisorOrganizationName ||
          'Unknown Organization';

        return (
          <Card
            key={invitation.invitationId}
            className="border border-gray-200 shadow-sm"
          >
            <CardContent className="flex items-center justify-between px-6 py-5">
              {/* Left side - Organization info */}
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900">{orgName}</h3>
                <p className="mt-0.5 text-sm text-gray-500">
                  From {supervisorName} ·{' '}
                  {format(new Date(invitation.createdDateTime), 'MMM d, yyyy')}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {invitation.enabledClientAccess
                    ? 'Client view/edit access requested'
                    : 'No client access requested'}
                </p>
              </div>

              {/* Right side - Action buttons */}
              <div className="ml-6 flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 border-gray-300 px-4 text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                  onClick={() => onDecline(invitation)}
                  disabled={isProcessing || isDisabled}
                  title={isDisabled ? 'Cannot decline invitations in admin mode' : undefined}
                >
                  {decliningInvitationId === invitation.invitationId ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Declining...
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4" />
                      Decline
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  className="bg-primary hover:bg-primary/90 gap-1.5 px-5"
                  onClick={() => onAccept(invitation)}
                  disabled={isProcessing || isDisabled}
                  title={isDisabled ? 'Cannot accept invitations in admin mode' : undefined}
                >
                  <Check className="h-4 w-4" />
                  Accept
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
